<template>
  <ModalRoot v-model="isOpen">
    <template #body="{ close }">
      <ModalBody
        :is-open="isOpen"
        hide-close-button
        mobile-full-screen
        class="!p-0 !bg-bg overflow-hidden w-full md:w-[500px] rounded-0 md:rounded-[35px] h-screen md:h-auto"
        @close="close"
      >
        <div
          class="p-6 bg-fill-brand-secondary backdrop-blur-[100px] flex flex-col gap-6 h-full md:h-auto"
        >
          <!-- Header -->
          <div class="h-10 justify-between items-center flex w-full">
            <h2 class="text-xl text-text font-serif">
              {{ $t("channel.leaderboard.actionPoints") }}
            </h2>
            <div
              class="w-10 h-10 bg-fill-brand-secondary rounded-full backdrop-blur-[35px] flex justify-center items-center cursor-pointer"
              @click="close"
            >
              <Icon icon="mdi:close" class="size-6 text-white" />
            </div>
          </div>

          <!-- Content -->
          <div
            class="flex-col justify-start items-start gap-4 inline-flex overflow-y-auto hide-scrollbar"
          >
            <!-- Add New Button -->
            <button
              class="self-stretch h-10 px-6 py-1 bg-fill-brand-secondary rounded-lg justify-center items-center gap-1 inline-flex hover:bg-fill-brand-secondary-hover transition-colors"
              @click="addNewAction"
            >
              <Icon icon="material-symbols:add" class="size-6 text-white" />
              <div
                class="text-center text-white text-sm font-medium leading-tight"
              >
                {{ $t("channel.leaderboard.addNewAction") }}
              </div>
            </button>

            <!-- Action Points List -->
            <div class="flex flex-col gap-4 w-full max-h-[510px]">
              <ActionPointsEditItem
                v-for="(action, i) in localActions"
                :key="`local-${i}`"
                is-new
                :item="action"
                @saved="() => onActionSaved(i, true)"
                @deleted="() => onActionDeleted(i, true)"
              />

              <ActionPointsEditItem
                v-for="(action, i) in savedActions"
                :key="`saved-${i}`"
                :item="action"
                :is-new="false"
                @saved="() => onActionSaved(i, false)"
                @deleted="() => onActionDeleted(i, false)"
              />
            </div>
          </div>
        </div>
      </ModalBody>
    </template>
  </ModalRoot>
</template>

<script lang="ts">
export interface LocalAppAction {
  name: string;
  points: number;
  title: string;
  short_title: string;
  notification_title?: string;
  is_logical: boolean;
}
</script>

<script setup lang="ts">
import { Icon } from "@iconify/vue";
import ModalBody from "@/components/ui/modal/modal-body.vue";
import ModalRoot from "@/components/ui/modal/modal-root.vue";
import ActionPointsEditItem from "./action-points-edit-item.vue";
import { computed, reactive, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { graphql as gql } from "@/generated";

const GetAppActionsDocument = gql(/* GraphQL */ `
  query GetAppActions {
    getAppActions {
      ... on AppActionForChannelAdminList {
        list {
          name
          title
          short_title
          notification_title
          points
          is_logical
        }
      }
    }
  }
`);

const isOpen = defineModel<boolean>("isOpen", { required: true });

const { result: appActionsResult, refetch: refetch } = useQuery(
  GetAppActionsDocument,
);
const localActions = reactive<LocalAppAction[]>([]);

const savedActions = computed(() => {
  const result = appActionsResult.value?.getAppActions;
  return result && "list" in result ? result.list : [];
});

function clearLocalActions() {
  localActions.splice(0, localActions.length);
}

function addNewAction() {
  localActions.unshift({
    name: "",
    points: 0,
    title: "",
    short_title: "",
    notification_title: "",
    is_logical: false,
  });
}

async function onActionSaved(index: number, isNew: boolean) {
  await refetch();

  if (isNew) {
    localActions.splice(index, 1);
  }
}

async function onActionDeleted(index: number, isNew: boolean) {
  if (isNew) {
    localActions.splice(index, 1);
  } else {
    await refetch();
  }
}

watch(isOpen, (value) => {
  if (!value) {
    clearLocalActions();
  }
});
</script>
